import React, { useState } from 'react';

/**
 * PlayerAvatar Component - FIXED VERSION
 * 
 * Displays player avatar with the following priority:
 * 1. Uploaded photo (if photoUrl exists)
 * 2. Initials (fallback)
 * 
 * Props:
 * - name: Player name (required)
 * - photoUrl: URL to uploaded photo (optional)
 * - size: Avatar size - 'sm' (28px), 'md' (40px), 'lg' (64px) (default: 'sm')
 * - style: Custom inline styles (optional) - overrides size prop
 * - onClick: Function (optional) - callback when avatar is clicked
 */
const PlayerAvatar = ({ 
  name, 
  photoUrl, 
  size = 'sm',
  style = {},
  onClick
}) => {
  const [imageError, setImageError] = useState(false);

  // Size configurations
  const sizeConfig = {
    sm: { dimension: 28, fontSize: 12 },
    md: { dimension: 40, fontSize: 16 },
    lg: { dimension: 64, fontSize: 24 }
  };

  const { dimension, fontSize } = sizeConfig[size] || sizeConfig.sm;

  // Calculate initials
  const nameStr = String(name || "P");
  const initials = (
    nameStr
      .split(" ")
      .map((s) => s && s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("") || "P"
  ).toUpperCase();

  // Base styles - can be overridden by style prop
  const baseStyle = {
    width: dimension,
    height: dimension,
    borderRadius: 9999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
    border: "2px solid #e5e7eb",
    cursor: onClick ? "pointer" : "default",
    transition: "transform 0.15s, box-shadow 0.15s",
    ...style  // Custom styles override defaults
  };

  // Hover effect style for clickable avatars
  const handleMouseEnter = (e) => {
    if (onClick) {
      e.currentTarget.style.transform = "scale(1.05)";
      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
    }
  };

  const handleMouseLeave = (e) => {
    if (onClick) {
      e.currentTarget.style.transform = "scale(1)";
      e.currentTarget.style.boxShadow = "none";
    }
  };

  // Extract fontSize from style prop if provided
  const finalFontSize = style.fontSize || fontSize;

  // Render uploaded photo
  if (photoUrl && !imageError) {
    return (
      <div 
        style={baseStyle}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        title={onClick ? "Click to view larger photo" : ""}
      >
        <img
          src={photoUrl}
          alt={name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover"
          }}
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  // Render initials (fallback)
  return (
    <div
      style={{
        ...baseStyle,
        background: "#e5f3ff",
        color: "#1e3a8a",
        fontSize: finalFontSize,
        fontWeight: 700
      }}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      title={onClick ? "Click to view larger" : ""}
    >
      {initials}
    </div>
  );
};

export default PlayerAvatar;
